import { 
  collection, 
  doc, 
  getDoc,
  addDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  increment 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Content Monitoring Service - Automatically detects and tracks influencer content
 */
const contentMonitoringService = {
  /**
   * Monitor influencer content across platforms
   * @param {string} campaignId - Campaign ID
   * @param {string} influencerId - Influencer ID
   * @param {string} trackingCode - Tracking code to look for
   * @returns {Promise<void>}
   */
  monitorInfluencerContent: async (campaignId, influencerId, trackingCode) => {
    try {
      // Get influencer's social media profiles
      const influencerData = await contentMonitoringService.getInfluencerSocialProfiles(influencerId);
      
      if (!influencerData) {
        console.warn('No influencer data found for monitoring:', influencerId);
        return;
      }

      // Monitor each platform
      const monitoringPromises = [];
      
      if (influencerData.socialMedia?.instagram) {
        monitoringPromises.push(
          contentMonitoringService.monitorInstagram(campaignId, influencerId, trackingCode, influencerData.socialMedia.instagram)
        );
      }
      
      if (influencerData.socialMedia?.tiktok) {
        monitoringPromises.push(
          contentMonitoringService.monitorTikTok(campaignId, influencerId, trackingCode, influencerData.socialMedia.tiktok)
        );
      }
      
      if (influencerData.socialMedia?.facebook) {
        monitoringPromises.push(
          contentMonitoringService.monitorFacebook(campaignId, influencerId, trackingCode, influencerData.socialMedia.facebook)
        );
      }

      await Promise.all(monitoringPromises);
      
    } catch (error) {
      console.error('Error monitoring influencer content:', error);
    }
  },

  /**
   * Get influencer's social media profiles
   * @param {string} influencerId - Influencer ID
   * @returns {Promise<object|null>} - Influencer data
   */
  getInfluencerSocialProfiles: async (influencerId) => {
    try {
      const userRef = doc(db, 'users', influencerId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return userSnap.data();
      }
      
      return null;
    } catch (error) {
      console.error('Error getting influencer profiles:', error);
      return null;
    }
  },

  /**
   * Monitor Instagram content (using Instagram Basic Display API)
   * @param {string} campaignId - Campaign ID
   * @param {string} influencerId - Influencer ID
   * @param {string} trackingCode - Tracking code
   * @param {string} instagramUrl - Instagram profile URL
   * @returns {Promise<void>}
   */
  monitorInstagram: async (campaignId, influencerId, trackingCode, instagramUrl) => {
    try {
      // Extract username from Instagram URL
      const username = contentMonitoringService.extractInstagramUsername(instagramUrl);
      if (!username) return;

      // This would typically use Instagram's API, but for now we'll simulate
      // In production, you'd need Instagram Basic Display API access

      
      // Simulate finding content with tracking link
      const mockContent = await contentMonitoringService.simulateInstagramScraping(username, trackingCode);
      
      if (mockContent.found) {
        await contentMonitoringService.recordContentDetection({
          campaignId,
          influencerId,
          trackingCode,
          platform: 'instagram',
          contentType: mockContent.type,
          contentUrl: mockContent.url,
          contentId: mockContent.id,
          detectedAt: new Date(),
          engagement: mockContent.engagement,
          reach: mockContent.reach
        });
      }
      
    } catch (error) {
      console.error('Error monitoring Instagram:', error);
    }
  },

  /**
   * Monitor TikTok content
   * @param {string} campaignId - Campaign ID
   * @param {string} influencerId - Influencer ID
   * @param {string} trackingCode - Tracking code
   * @param {string} tiktokUrl - TikTok profile URL
   * @returns {Promise<void>}
   */
  monitorTikTok: async (campaignId, influencerId, trackingCode, tiktokUrl) => {
    try {
      const username = contentMonitoringService.extractTikTokUsername(tiktokUrl);
      if (!username) return;


      
      // Simulate TikTok content detection
      const mockContent = await contentMonitoringService.simulateTikTokScraping(username, trackingCode);
      
      if (mockContent.found) {
        await contentMonitoringService.recordContentDetection({
          campaignId,
          influencerId,
          trackingCode,
          platform: 'tiktok',
          contentType: 'video',
          contentUrl: mockContent.url,
          contentId: mockContent.id,
          detectedAt: new Date(),
          engagement: mockContent.engagement,
          reach: mockContent.views
        });
      }
      
    } catch (error) {
      console.error('Error monitoring TikTok:', error);
    }
  },

  /**
   * Monitor Facebook content
   * @param {string} campaignId - Campaign ID
   * @param {string} influencerId - Influencer ID
   * @param {string} trackingCode - Tracking code
   * @param {string} facebookUrl - Facebook profile URL
   * @returns {Promise<void>}
   */
  monitorFacebook: async (campaignId, influencerId, trackingCode, facebookUrl) => {
    try {

      
      // Facebook Graph API would be used here in production
      const mockContent = await contentMonitoringService.simulateFacebookScraping(facebookUrl, trackingCode);
      
      if (mockContent.found) {
        await contentMonitoringService.recordContentDetection({
          campaignId,
          influencerId,
          trackingCode,
          platform: 'facebook',
          contentType: mockContent.type,
          contentUrl: mockContent.url,
          contentId: mockContent.id,
          detectedAt: new Date(),
          engagement: mockContent.engagement,
          reach: mockContent.reach
        });
      }
      
    } catch (error) {
      console.error('Error monitoring Facebook:', error);
    }
  },

  /**
   * Record detected content in database
   * @param {object} contentData - Content detection data
   * @returns {Promise<void>}
   */
  recordContentDetection: async (contentData) => {
    try {
      // Store in contentDetections collection
      const detectionsRef = collection(db, 'contentDetections');
      await addDoc(detectionsRef, {
        ...contentData,
        detectedAt: serverTimestamp(),
        status: 'active',
        lastChecked: serverTimestamp()
      });

      // Update tracking link analytics
      const trackingLinksRef = collection(db, 'trackingLinks');
      const q = query(
        trackingLinksRef,
        where('trackingCode', '==', contentData.trackingCode),
        where('campaignId', '==', contentData.campaignId)
      );

      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach(async (doc) => {
        await updateDoc(doc.ref, {
          'analytics.contentDetections': increment(1),
          'analytics.lastContentDetected': serverTimestamp(),
          [`analytics.${contentData.platform}Reach`]: contentData.reach || 0,
          [`analytics.${contentData.platform}Engagement`]: contentData.engagement || 0
        });
      });


      
    } catch (error) {
      console.error('Error recording content detection:', error);
    }
  },

  /**
   * Extract Instagram username from URL
   * @param {string} url - Instagram URL
   * @returns {string|null} - Username
   */
  extractInstagramUsername: (url) => {
    try {
      const match = url.match(/instagram\.com\/([^/?]+)/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  },

  /**
   * Extract TikTok username from URL
   * @param {string} url - TikTok URL
   * @returns {string|null} - Username
   */
  extractTikTokUsername: (url) => {
    try {
      const match = url.match(/tiktok\.com\/@([^/?]+)/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  },

  /**
   * Simulate Instagram content scraping (replace with real API in production)
   * @param {string} username - Instagram username
   * @param {string} trackingCode - Tracking code to search for
   * @returns {Promise<object>} - Mock content data
   */
  simulateInstagramScraping: async (username, trackingCode) => {
    // In production, this would use Instagram Basic Display API
    // For now, simulate finding content
    const random = Math.random();
    
    if (random > 0.7) { // 30% chance of finding content
      return {
        found: true,
        type: random > 0.85 ? 'story' : 'post',
        url: `https://instagram.com/p/mock_${trackingCode}`,
        id: `mock_${trackingCode}_${Date.now()}`,
        engagement: Math.floor(Math.random() * 1000) + 100,
        reach: Math.floor(Math.random() * 10000) + 1000
      };
    }
    
    return { found: false };
  },

  /**
   * Simulate TikTok content scraping
   * @param {string} username - TikTok username
   * @param {string} trackingCode - Tracking code to search for
   * @returns {Promise<object>} - Mock content data
   */
  simulateTikTokScraping: async (username, trackingCode) => {
    const random = Math.random();
    
    if (random > 0.6) { // 40% chance of finding content
      return {
        found: true,
        url: `https://tiktok.com/@${username}/video/mock_${trackingCode}`,
        id: `tiktok_${trackingCode}_${Date.now()}`,
        engagement: Math.floor(Math.random() * 5000) + 500,
        views: Math.floor(Math.random() * 50000) + 5000
      };
    }
    
    return { found: false };
  },

  /**
   * Simulate Facebook content scraping
   * @param {string} profileUrl - Facebook profile URL
   * @param {string} trackingCode - Tracking code to search for
   * @returns {Promise<object>} - Mock content data
   */
  simulateFacebookScraping: async (profileUrl, trackingCode) => {
    const random = Math.random();
    
    if (random > 0.8) { // 20% chance of finding content
      return {
        found: true,
        type: 'post',
        url: `https://facebook.com/mock_post_${trackingCode}`,
        id: `fb_${trackingCode}_${Date.now()}`,
        engagement: Math.floor(Math.random() * 2000) + 200,
        reach: Math.floor(Math.random() * 15000) + 2000
      };
    }
    
    return { found: false };
  },

  /**
   * Schedule content monitoring for all active campaigns
   * @returns {Promise<void>}
   */
  scheduleContentMonitoring: async () => {
    try {
      // Get all active tracking links
      const trackingLinksRef = collection(db, 'trackingLinks');
      const q = query(trackingLinksRef, where('isActive', '==', true));
      
      const querySnapshot = await getDocs(q);
      const monitoringPromises = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        monitoringPromises.push(
          contentMonitoringService.monitorInfluencerContent(
            data.campaignId,
            data.influencerId,
            data.trackingCode
          )
        );
      });
      
      await Promise.all(monitoringPromises);

      
    } catch (error) {
      console.error('Error scheduling content monitoring:', error);
    }
  },

  /**
   * Get content detection analytics for a campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Content detection analytics
   */
  getContentDetectionAnalytics: async (campaignId) => {
    try {
      const detectionsRef = collection(db, 'contentDetections');
      const q = query(detectionsRef, where('campaignId', '==', campaignId));
      
      const querySnapshot = await getDocs(q);
      const detections = [];
      let totalReach = 0;
      let totalEngagement = 0;
      const platformBreakdown = {};
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        detections.push({ id: doc.id, ...data });
        
        totalReach += data.reach || 0;
        totalEngagement += data.engagement || 0;
        
        platformBreakdown[data.platform] = (platformBreakdown[data.platform] || 0) + 1;
      });
      
      return {
        totalDetections: detections.length,
        totalReach,
        totalEngagement,
        platformBreakdown,
        detections: detections.slice(0, 10) // Return latest 10 detections
      };
      
    } catch (error) {
      console.error('Error getting content detection analytics:', error);
      return {
        totalDetections: 0,
        totalReach: 0,
        totalEngagement: 0,
        platformBreakdown: {},
        detections: []
      };
    }
  }
};

export default contentMonitoringService;
